/**
 * i18n mínimo y tipado. es-PE es el locale por defecto; para sumar locales,
 * registra otro diccionario con la MISMA forma en LOCALES. `t(key, params)`
 * interpola {var}. Evitamos dependencias externas: es liviano y Capacitor-ready.
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { esPE, type Dictionary, type MessageKey } from "./locales/es-PE.js";

export type Locale = "es-PE";
const LOCALES: Record<Locale, Dictionary> = { "es-PE": esPE };
const DEFAULT_LOCALE: Locale = "es-PE";

export type TranslateParams = Record<string, string | number>;
export type Translate = (key: MessageKey, params?: TranslateParams) => string;

interface I18nValue {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: Translate;
}

const I18nContext = createContext<I18nValue | null>(null);

function interpolate(template: string, params?: TranslateParams): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (_m, k: string) =>
    k in params ? String(params[k]) : `{${k}}`,
  );
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocale] = useState<Locale>(DEFAULT_LOCALE);
  const t = useCallback<Translate>(
    (key, params) => {
      const dict = LOCALES[locale] ?? LOCALES[DEFAULT_LOCALE];
      return interpolate(dict[key] ?? key, params);
    },
    [locale],
  );
  const value = useMemo<I18nValue>(() => ({ locale, setLocale, t }), [locale, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n fuera de I18nProvider");
  return ctx;
}

/** Azúcar: solo la función de traducción. */
export function useT(): Translate {
  return useI18n().t;
}

export type { MessageKey };
